
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { KendoAlertService, KendoGridStateDirective } from '@ticomix/angular-kendo';
import { FormsModule } from '@angular/forms';
import { HttpValidationProblemDetails, InfoRequest, ManageClient, ProfileViewModel } from '../../api_identity/api';
import { getMessage } from '@ticomix/common';
import { CommonModule } from '@angular/common';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { IdentityAuthService } from '../../_services/identity-auth.service';

interface PasswordOptions {
    requiredLength: number;
    requiredUniqueChars: number;
    requireDigit: boolean;
    requireLowercase: boolean;
    requireNonAlphanumeric: boolean;
    requireUppercase: boolean;
}

interface PasswordCheck {
    label: string;
    met: boolean;
}

function generateRandomPassword(opts?: PasswordOptions): string {
    if (opts == null) opts = {
        requiredLength: 8,
        requiredUniqueChars: 4,
        requireDigit: true,
        requireLowercase: true,
        requireNonAlphanumeric: true,
        requireUppercase: true
    };

    const randomChars = [
        "ABCDEFGHJKLMNOPQRSTUVWXYZ",
        "abcdefghijkmnopqrstuvwxyz",
        "0123456789",
        "!@$?_-"
    ];

    let chars: string[] = [];

    if (opts.requireUppercase) {
        let rand1 = Math.floor(Math.random() * chars.length);
        let rand2 = Math.floor(Math.random() * randomChars[0].length);
        chars.splice(rand1, 0, randomChars[0][rand2]);
    }

    if (opts.requireLowercase) {
        let rand1 = Math.floor(Math.random() * chars.length);
        let rand2 = Math.floor(Math.random() * randomChars[1].length);
        chars.splice(rand1, 0, randomChars[1][rand2]);
    }

    if (opts.requireDigit) {
        let rand1 = Math.floor(Math.random() * chars.length);
        let rand2 = Math.floor(Math.random() * randomChars[2].length);
        chars.splice(rand1, 0, randomChars[2][rand2]);
    }

    if (opts.requireNonAlphanumeric) {
        let rand1 = Math.floor(Math.random() * chars.length);
        let rand2 = Math.floor(Math.random() * randomChars[3].length);
        chars.splice(rand1, 0, randomChars[3][rand2]);
    }

    for (let i = chars.length; i < opts.requiredLength || new Set(chars).size < opts.requiredUniqueChars; i++) {
        let rand1 = Math.floor(Math.random() * chars.length);
        let rand3 = Math.floor(Math.random() * randomChars.length);
        let rcs = randomChars[rand3];
        let rand2 = Math.floor(Math.random() * rcs.length);
        chars.splice(rand1, 0, rcs[rand2]);
    }

    return chars.join('');
}

@Component({
    selector: 'profile',
    templateUrl: './profile.component.html',
    imports: [
        CommonModule,
        FormsModule,
        DialogsModule
    ]
})
export class ProfileComponent implements OnInit {
    constructor(
        private identityClient: ManageClient,
        private authService: IdentityAuthService,
        private router: Router,
        private kendoAlert: KendoAlertService
    ) { }

    user: ProfileViewModel = new ProfileViewModel();
    busy: boolean = false;

    passwordOptions: PasswordOptions = {
        requiredLength: 8,
        requiredUniqueChars: 4,
        requireDigit: true,
        requireLowercase: true,
        requireNonAlphanumeric: true,
        requireUppercase: true
    };

    resetPassword = {
        show: false,
        top: 100,
        left: 200,
        newPassword: '',
    };

    async ngOnInit() {
        this.user = await firstValueFrom(this.identityClient.infoGet());
    }

    async save() {
        this.busy = true;
        try {
            await firstValueFrom(this.identityClient.infoPost(this.user));
            await this.kendoAlert.alertAsync('Profile updated successfully!');
            this.router.navigate(['/identity/profile'], { queryParamsHandling: 'preserve' });
        } catch (ex) {
            if (ex instanceof HttpValidationProblemDetails) {
                let message = getMessage(ex);
                await this.kendoAlert.alertAsync(message);
            }
        } finally {
            this.busy = false;
        }
    }

    getPasswordChecks(password: string | null | undefined): PasswordCheck[] {
        const pw = password ?? '';
        const checks: PasswordCheck[] = [
            { label: `At least ${this.passwordOptions.requiredLength} characters`, met: pw.length >= this.passwordOptions.requiredLength },
        ];
        if (this.passwordOptions.requireUppercase) {
            checks.push({ label: 'At least 1 uppercase letter', met: /[A-Z]/.test(pw) });
        }
        if (this.passwordOptions.requireLowercase) {
            checks.push({ label: 'At least 1 lowercase letter', met: /[a-z]/.test(pw) });
        }
        if (this.passwordOptions.requireDigit) {
            checks.push({ label: 'At least 1 digit', met: /[0-9]/.test(pw) });
        }
        if (this.passwordOptions.requireNonAlphanumeric) {
            checks.push({ label: 'At least 1 special character', met: /[^a-zA-Z0-9]/.test(pw) });
        }
        checks.push({ label: `At least ${this.passwordOptions.requiredUniqueChars} unique characters`, met: new Set(pw).size >= this.passwordOptions.requiredUniqueChars });
        return checks;
    }

    isPasswordValid(password: string | null | undefined): boolean {
        return this.getPasswordChecks(password).every(check => check.met);
    }

    openResetPasswordWindow() {
        this.resetPassword.newPassword = '';
        this.resetPassword.show = true;
    }

    closeResetPasswordWindow() {
        this.resetPassword.show = false;
    }

    shufflePassword() {
        this.resetPassword.newPassword = generateRandomPassword();
    }

    async saveResetPassword() {
        try {
            await firstValueFrom(this.identityClient.changePassword(new InfoRequest({
                NewPassword: this.resetPassword.newPassword
            })));
            this.resetPassword.show = false;
            await this.authService.signOut();
            this.router.navigate(['/identity/signin']);
        } catch (ex) {
            if (ex instanceof HttpValidationProblemDetails) {
                let message = getMessage(ex);
                await this.kendoAlert.alertAsync(message);
            }
        }
    }

    async resetGridSettings() {
        if (await this.kendoAlert.confirmAsync('Are you sure you want to reset the grid settings?')) {
            KendoGridStateDirective.clearAllStates();
            localStorage.removeItem('myBasinGridColumns');
        }
    }
}
